import {
  ArrayStringFilterData,
  ArrayStringFilterDataSchema,
} from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { FilterOperator } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { HoldStatusStrategy } from "./hold-status-strategy";
import {
  OrderEditingDisallowEvent,
  OrderEditingDisallowEventZod,
} from "./order-editing-disallow-event";

/**
 * Hand-authored projection of the persisted order editing settings. Keep in
 * sync with the generated `OrderEditingSettingsRaw` and its sub-schemas in
 * `redo/backend/db/schema/src/types/team/OrderEditingSettings.schema.d.ts`.
 *
 * Note on `filters`/`rules`: the generated `*Raw` types them loosely as
 * `any[] | null` (the Mongoose schema stores them as `[Object]`). This slice
 * deliberately keeps the strong hand-authored `OrderEditingFilter[]` /
 * `OrderEditingRule[]` types because consumers (`order-editing/service`, the
 * match helper, `migrate-order-editing-rules`) rely on them.
 */
export interface OrderEditingTools {
  cancelOrder: {
    enabled: boolean;
    cancelOrderTimeWindowAfterPlacingMinutes: number;
    requireConfirmation: boolean;
    omsEventsToDisallowUpon?: OrderEditingDisallowEvent[];
    restockInventory?: boolean;
  };
  changeAddress: {
    enabled: boolean;
    changeOrderAddressTimeWindowAfterPlacingMinutes: number;
    requireConfirmation: boolean;
    omsEventsToDisallowUpon?: OrderEditingDisallowEvent[];
  };
  addOrderDiscount: {
    enabled: boolean;
    addOrderDiscountTimeWindowAfterPlacingMinutes: number;
    omsEventsToDisallowUpon?: OrderEditingDisallowEvent[];
  };
  addOrderGiftNote: {
    enabled: boolean;
    addOrderGiftNoteTimeWindowAfterPlacingMinutes: number;
    omsEventsToDisallowUpon?: OrderEditingDisallowEvent[];
  };
  updateOrder: {
    enabled: boolean;
    updateOrderTimeWindowAfterPlacingMinutes: number;
    addItem: boolean;
    removeItem: boolean;
    changeVariant: boolean;
    omsEventsToDisallowUpon?: OrderEditingDisallowEvent[];
    removeItemRestockInventory?: boolean;
    changeVariantRestockInventory?: boolean;
  };
}

const OrderEditingToolsSchema_ = z.object({
  cancelOrder: z.object({
    enabled: z.boolean(),
    cancelOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0),
    requireConfirmation: z.boolean(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).optional(),
    restockInventory: z.boolean().optional(),
  }),
  changeAddress: z.object({
    enabled: z.boolean(),
    changeOrderAddressTimeWindowAfterPlacingMinutes: z.number().int().min(0),
    requireConfirmation: z.boolean(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).optional(),
  }),
  addOrderDiscount: z.object({
    enabled: z.boolean(),
    addOrderDiscountTimeWindowAfterPlacingMinutes: z.number().int().min(0),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).optional(),
  }),
  addOrderGiftNote: z.object({
    enabled: z.boolean(),
    addOrderGiftNoteTimeWindowAfterPlacingMinutes: z.number().int().min(0),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).optional(),
  }),
  updateOrder: z.object({
    enabled: z.boolean(),
    updateOrderTimeWindowAfterPlacingMinutes: z.number().int().min(0),
    addItem: z.boolean(),
    removeItem: z.boolean(),
    changeVariant: z.boolean(),
    omsEventsToDisallowUpon: z.array(OrderEditingDisallowEventZod).optional(),
    removeItemRestockInventory: z.boolean().optional(),
    changeVariantRestockInventory: z.boolean().optional(),
  }),
});
export const OrderEditingToolsSchema: z.ZodType<OrderEditingTools> =
  OrderEditingToolsSchema_;

export interface OrderEditingEnabledPlatforms {
  shopperAI: boolean;
  supportSelfServe: boolean;
  customerPortal: boolean;
  orderTracking: boolean;
}

export const DEFAULT_PAYMENT_COLLECTION_WINDOW_MINUTES = 60 * 24;

export function normalizePaymentCollectionWindowMinutes(
  value: number | null | undefined,
): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 1
    ? value
    : DEFAULT_PAYMENT_COLLECTION_WINDOW_MINUTES;
}

export interface OrderEditingHolds {
  applyHoldToAllOrders: boolean;
  holdDurationInMinutes: number;
  holdOrdersRequiringPayment: boolean;
  holdStatusStrategy?: HoldStatusStrategy;
  /** Auto-undo unpaid extras (additions, quantity increases, unpaid swaps) after the window. */
  autoRevertUnpaidEdits?: boolean;
  /** Minutes to wait for the extra-invoice payment before auto-reverting. */
  paymentCollectionWindowMinutes?: number;
}

// Define the filter names we support for order editing
export enum OrderEditingFilterName {
  ORDER_TAGS = "order_tags",
  ORDER_ID = "order_id",
}

export const OrderEditingFilterNameZod = createEnum([
  OrderEditingFilterName.ORDER_TAGS,
  OrderEditingFilterName.ORDER_ID,
]);

// Create a constrained schema that only allows our specific filter names
const OrderEditingFilterSchema_ = ArrayStringFilterDataSchema.extend({
  name: OrderEditingFilterNameZod,
});
export const OrderEditingFilterSchema: z.ZodType<OrderEditingFilter> =
  OrderEditingFilterSchema_;
export interface OrderEditingFilter extends Omit<
  ArrayStringFilterData,
  "name"
> {
  name: OrderEditingFilterName;
}

/**
 * A rule's match condition: a flat list of filters combined with a single
 * boolean operator. AND requires every filter to match; OR requires any.
 * An empty filter list always matches (used by the catch-all default rule).
 */
export interface OrderEditingRuleMatch {
  operator: FilterOperator;
  filters: OrderEditingFilter[];
}

/**
 * A single order editing rule. The first rule (in order) whose `match` passes
 * for a given order supplies that order's enabled actions and time windows.
 * The last rule should be a catch-all default (empty filters) so every order
 * resolves to some configuration.
 */
export interface OrderEditingRule {
  id: string;
  name?: string;
  match: OrderEditingRuleMatch;
  tools: OrderEditingTools;
}

const OrderEditingRuleMatchSchema_ = z.object({
  operator: createEnum(FilterOperator, { enumName: "FilterOperator" }),
  filters: z.array(OrderEditingFilterSchema),
});
export const OrderEditingRuleMatchSchema: z.ZodType<OrderEditingRuleMatch> =
  OrderEditingRuleMatchSchema_;

const OrderEditingRuleSchema_ = z.object({
  id: z.string(),
  name: z.string().optional(),
  match: OrderEditingRuleMatchSchema,
  tools: OrderEditingToolsSchema,
});
export const OrderEditingRuleSchema: z.ZodType<OrderEditingRule> =
  OrderEditingRuleSchema_;

export interface OrderEditingSettings {
  subscriptionEditing?: {
    enabled?: boolean | null;
    quantity?: boolean | null;
    frequency?: boolean | null;
    nextDate?: boolean | null;
    skip?: boolean | null;
    cancel?: boolean | null;
    address?: boolean | null;
  } | null;
  holds?: OrderEditingHolds | null;
  tools?: OrderEditingTools | null;
  useOmsEvents?: boolean;
  mergeFulfillmentsOnOrderEdit?: boolean;
  updateShippingPriceOnOrderEdit?: boolean;
  enabledPlatforms?: OrderEditingEnabledPlatforms;
  /** @deprecated Legacy global gate. Use per-rule `rules[].match` instead. */
  filters?: OrderEditingFilter[] | null;
  /**
   * Ordered list of tag-based rules. The first rule whose match passes for an
   * order determines its enabled actions and time windows. When empty, the
   * legacy `filters` + `tools` behavior applies.
   */
  rules?: OrderEditingRule[] | null;
}
